import { auth } from '../config/firebase';
import { config } from '../config';
import type { ImproveItemCurrent, OnboardingStage } from '../types/onboarding';

// ---------------------------------------------------------------------------
// Server → Client message types
// ---------------------------------------------------------------------------

type ServerMessageType =
  | 'session_started'
  | 'question'
  | 'item_pending'
  | 'item_drafted'
  | 'draft_error'
  | 'stage_complete'
  | 'session_complete'
  | 'state_conflict'
  | 'error';

interface OnboardingMessage {
  type: ServerMessageType;
  data?: any;
}

// ---------------------------------------------------------------------------
// Server → Client payloads
// ---------------------------------------------------------------------------

export interface SessionStartedData {
  sessionId: string;
  draftTaskId: string;
  stages: OnboardingStage[];
  currentStage: OnboardingStage;
  /** One-time orientation, sent only on session_started — explains what
   *  this conversation is and why it's worth answering, before any stage
   *  intro or question. */
  sessionWelcome: string;
  stageIntro: string;
  /** True when this session picked up from a prior incomplete attempt
   *  (different stage, or some company_context fields already answered)
   *  rather than starting completely fresh. */
  resumed: boolean;
  /** The onboardingturnstate document's CAS version at session start — the
   *  service tracks this internally and stamps it onto every subsequent
   *  submitTurn() call as `expectedStateVersion`; nothing in this file's
   *  consumers needs to read it directly. */
  stateVersion?: number;
}

export interface QuestionData {
  stage: OnboardingStage;
  question: string;
  progressPercent: number;
  /** Set only on the first question of a stage — the stage's orientation
   *  line, shown once per stage rather than repeated on every question. */
  stageIntro: string | null;
  stateVersion?: number;
  /** Present when this question was folded into the same outgoing batch
   *  as a submit_turn's own response (see the backend's
   *  _handle_submit_turn) — lets a consumer correlate it back to the
   *  specific answer that produced it, same as the other turn-response
   *  payloads below. Absent for a question sent outside a turn response
   *  (e.g. the very first question after 'ready', or after a topic change). */
  clientEventId?: string;
}

/**
 * Shape of `draft` varies by stage:
 *  - company_context: { field, value }
 *  - objections / faqs: { documentId, question, answer, variations: string[] }
 *  - landmines: { documentId, trigger, canSay: {point, context}[],
 *                 cannotSay: {point, reason, severity}[], approvalStatus: 'pending' }
 *  - competitors: { documentId, name, advantages: string[], weaknesses: string[] }
 * Left loose here — the consuming wizard component narrows by `stage`.
 */
export interface ItemDraftedData {
  stage: OnboardingStage;
  draft: Record<string, any>;
  progressPercent: number;
  stateVersion?: number;
  /** The clientEventId of the submit_turn that produced this item — lets
   *  OnboardingWizard.tsx mark the matching optimistic history entry as
   *  accepted rather than only clearing a global "submitting" flag. */
  clientEventId?: string;
}

export interface ItemPendingData {
  stage: OnboardingStage;
  draft: Record<string, any>;
  /** Required missing-half question owned by the server. Carrying it on the
   * item event lets the UI keep the answer box active even if a subsequent
   * question frame is delayed or a mixed-version server emits stale
   * completion frames. */
  followupQuestion?: string | null;
  progressPercent: number;
  stateVersion?: number;
  clientEventId?: string;
}

export interface DraftErrorData {
  message: string;
  /** The clientEventId of the submit_turn that failed — lets
   *  OnboardingWizard.tsx mark that SPECIFIC history entry as failed
   *  instead of only showing the unrelated global error banner. */
  clientEventId?: string;
}

export interface StageCompleteData {
  finishedStage: OnboardingStage;
  nextStage: OnboardingStage | null;
  progressPercent: number;
}

export interface SessionCompleteData {
  itemsCaptured: number;
  draftTaskId?: string;
}

export interface StateConflictData {
  message: string;
  stateVersion: number;
  clientEventId?: string;
}

// ---------------------------------------------------------------------------
// Service class
// ---------------------------------------------------------------------------

class OnboardingWebSocketService {
  private socket: WebSocket | null = null;
  private reconnectAttempts = 0;
  private readonly maxReconnectAttempts = 5;
  private readonly reconnectDelay = 2000;
  private isManualDisconnect = false;
  private currentSessionId: string | null = null;
  /** Preserved across reconnect attempts so a dropped connection resumes the
   *  same session rather than starting a fresh one. */
  private requestedSessionId: string | undefined = undefined;
  private requestedDraftTaskId: string | undefined = undefined;
  /**
   * Incremented on every connect() call; each call's socket handlers capture
   * the epoch that was current when THEY were created and check it before
   * acting. Without this, React 18 StrictMode's dev-only double-invoke of
   * mount effects (mount -> cleanup -> mount again) opens two sockets in
   * quick succession, and the first one's async onopen (it awaits
   * getIdToken() before sending start_session) can still complete AFTER the
   * second connect() has already reassigned `this.socket` — sending
   * start_session down whichever socket `this.socket` happens to point to
   * by then, not the one that actually opened. That's what produced two
   * server-side sessions from one wizard mount with the visible one stuck
   * on a blank screen. Every stale-epoch socket becomes fully inert instead.
   */
  private connectionEpoch = 0;
  /**
   * The onboardingturnstate document's CAS version this connection last
   * observed — stamped into every submitTurn() call as expectedStateVersion.
   * Updated from whichever incoming message happens to carry a fresher
   * `stateVersion` (session_started, question, item_pending, item_drafted,
   * state_conflict); messages that don't carry one (draft_error, stage_complete,
   * ...) leave it untouched.
   */
  private currentStateVersion = 0;

  // Event handlers — server → client
  private onSessionStartedHandler: ((data: SessionStartedData) => void) | null = null;
  private onQuestionHandler: ((data: QuestionData) => void) | null = null;
  private onItemPendingHandler: ((data: ItemPendingData) => void) | null = null;
  private onItemDraftedHandler: ((data: ItemDraftedData) => void) | null = null;
  private onDraftErrorHandler: ((data: DraftErrorData) => void) | null = null;
  private onStageCompleteHandler: ((data: StageCompleteData) => void) | null = null;
  private onSessionCompleteHandler: ((data: SessionCompleteData) => void) | null = null;
  private onStateConflictHandler: ((data: StateConflictData) => void) | null = null;
  private onErrorHandler: ((error: string) => void) | null = null;
  private onConnectionChangeHandler: ((connected: boolean) => void) | null = null;

  // ---------------------------------------------------------------------------
  // Connection management
  // ---------------------------------------------------------------------------

  connect(sessionId?: string, draftTaskId?: string): Promise<void> {
    // Superseding a still-open socket from a previous call (StrictMode's
    // double-invoke, or a caller reconnecting without disconnecting first) —
    // close it as a plain teardown, not a "manual disconnect" (that flag
    // controls auto-reconnect semantics for the NEW socket, unrelated here)
    // and not through disconnect() (that also clears requestedSessionId,
    // which the fresh connect() below is about to set from its own args).
    if (this.socket && this.socket.readyState !== WebSocket.CLOSED) {
      this.socket.onopen = null;
      this.socket.onmessage = null;
      this.socket.onerror = null;
      this.socket.onclose = null;
      this.socket.close();
    }

    const epoch = ++this.connectionEpoch;

    return new Promise((resolve, reject) => {
      this.isManualDisconnect = false;
      this.requestedSessionId = sessionId ?? this.requestedSessionId;
      this.requestedDraftTaskId = draftTaskId ?? this.requestedDraftTaskId;

      const wsUrl = config.websocket.onboardingUrl;
      const socket = new WebSocket(wsUrl);
      this.socket = socket;

      const isCurrent = () => epoch === this.connectionEpoch;

      socket.onopen = async () => {
        if (!isCurrent()) return;
        this.reconnectAttempts = 0;
        this.onConnectionChangeHandler?.(true);

        try {
          const token = await auth.currentUser?.getIdToken();
          if (!isCurrent()) return; // superseded while the token fetch was in flight

          if (!token) {
            throw new Error('No authentication token available');
          }

          socket.send(
            JSON.stringify({
              type: 'start_session',
              data: {
                token,
                sessionId: this.requestedSessionId,
                draftTaskId: this.requestedDraftTaskId,
              },
            })
          );
          resolve();
        } catch (error) {
          if (isCurrent()) reject(error);
        }
      };

      socket.onmessage = (event) => {
        if (!isCurrent()) return;
        try {
          const message: OnboardingMessage = JSON.parse(event.data);
          this.handleServerMessage(message);
        } catch (error) {
          // Non-fatal parse error — log and continue
          console.error('OnboardingWebSocketService: failed to parse message', error);
        }
      };

      socket.onerror = (error) => {
        if (!isCurrent()) return;
        this.onConnectionChangeHandler?.(false);
        reject(error);
      };

      socket.onclose = () => {
        if (!isCurrent()) return;
        this.onConnectionChangeHandler?.(false);

        if (!this.isManualDisconnect && this.reconnectAttempts < this.maxReconnectAttempts) {
          this.reconnectAttempts++;
          const delay = this.reconnectDelay * this.reconnectAttempts;
          setTimeout(() => {
            if (!isCurrent()) return;
            this.connect(this.currentSessionId ?? this.requestedSessionId, this.requestedDraftTaskId).catch(() => {
              // Reconnect failure is logged internally; callers rely on onConnectionChange
            });
          }, delay);
        }
      };

      setTimeout(() => {
        if (!isCurrent()) return;
        if (socket.readyState !== WebSocket.OPEN) {
          reject(new Error('Connection timeout'));
        }
      }, 10000);
    });
  }

  disconnect(): void {
    // Invalidates any in-flight connect() call too (e.g. one still awaiting
    // getIdToken() when disconnect() is called), not just the current socket.
    this.connectionEpoch++;
    this.isManualDisconnect = true;
    this.currentSessionId = null;
    this.currentStateVersion = 0;
    this.requestedSessionId = undefined;
    this.requestedDraftTaskId = undefined;

    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }

    this.onConnectionChangeHandler?.(false);
  }

  // ---------------------------------------------------------------------------
  // Message dispatch
  // ---------------------------------------------------------------------------

  private handleServerMessage(message: OnboardingMessage): void {
    // Any payload carrying a fresher stateVersion updates what the NEXT
    // submitTurn() call declares as expectedStateVersion — see this
    // field's own doc comment. Applied uniformly before the per-type
    // dispatch below so no case has to remember to do it itself.
    if (typeof message.data?.stateVersion === 'number') {
      this.currentStateVersion = message.data.stateVersion;
    }

    switch (message.type) {
      case 'session_started':
        this.currentSessionId = message.data?.sessionId ?? null;
        this.onSessionStartedHandler?.(message.data);
        break;

      case 'question':
        this.onQuestionHandler?.(message.data);
        break;

      case 'item_pending':
        this.onItemPendingHandler?.(message.data);
        break;

      case 'item_drafted':
        this.onItemDraftedHandler?.(message.data);
        break;

      case 'draft_error':
        this.onDraftErrorHandler?.(message.data);
        break;

      case 'stage_complete':
        this.onStageCompleteHandler?.(message.data);
        break;

      case 'session_complete':
        this.onSessionCompleteHandler?.(message.data);
        break;

      case 'state_conflict':
        this.onStateConflictHandler?.(message.data);
        break;

      case 'error':
        {
          const errorMessage = message.data?.message ?? 'Unknown onboarding error';
          this.onErrorHandler?.(errorMessage);

          // Auth/role failures are terminal for this browser session. Leaving
          // auto-reconnect enabled loops through the same rejection and makes
          // the persistent guide look blank or unstable.
          if (/admin access required|authentication|required token|invalid token/i.test(errorMessage)) {
            this.isManualDisconnect = true;
            this.onConnectionChangeHandler?.(false);
            this.socket?.close();
          }
        }
        break;

      default: {
        // Exhaustiveness guard: TypeScript narrows to never here if all cases are handled
        const _exhaustive: never = message.type;
        console.warn('OnboardingWebSocketService: unhandled message type', _exhaustive);
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Client → Server messages
  // ---------------------------------------------------------------------------

  /** Triggers first question delivery once the customer is ready to begin. */
  sendReady(): void {
    this.sendRaw({ type: 'ready' });
  }

  /**
   * Single-message turn submission — replaces the old two-message
   * sendResponse() + submitAndNext() pair. `clientEventId` must be
   * generated by the caller (crypto.randomUUID()) ONCE per logical user
   * action and reused verbatim on any retry of that same action (e.g. the
   * same click retried after a dropped connection) — that's what lets the
   * server recognize a retried submission as the same turn and resend its
   * cached response instead of reprocessing (see the backend's
   * _handle_submit_turn). expectedStateVersion is this service's own
   * last-observed onboardingturnstate version; a stale value gets a
   * `state_conflict` response rather than silently clobbering a
   * concurrent writer.
   */
  submitTurn(clientEventId: string, answer: string, inputSource: 'typed' | 'voice' = 'typed'): void {
    this.sendRaw({
      type: 'submit_turn',
      data: { clientEventId, expectedStateVersion: this.currentStateVersion, answer, inputSource },
    });
  }

  /** Focuses the existing conversation on any topic, including an earlier
   * one. The backend owns the follow-up question and completion logic. */
  focusTopic(stage: OnboardingStage, pendingQuestion?: string): boolean {
    return this.sendRaw({
      type: 'focus_topic',
      data: { stage, ...(pendingQuestion ? { pendingQuestion } : {}) },
    });
  }

  finish(): void {
    this.sendRaw({ type: 'finish_session' });
  }

  private sendRaw(message: { type: string; data?: any }): boolean {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      console.error('OnboardingWebSocketService: cannot send — socket not open', message.type);
      return false;
    }
    this.socket.send(JSON.stringify(message));
    return true;
  }

  // ---------------------------------------------------------------------------
  // Event handler setters
  // ---------------------------------------------------------------------------

  onSessionStarted(handler: (data: SessionStartedData) => void): void {
    this.onSessionStartedHandler = handler;
  }

  onQuestion(handler: (data: QuestionData) => void): void {
    this.onQuestionHandler = handler;
  }

  onItemPending(handler: (data: ItemPendingData) => void): void {
    this.onItemPendingHandler = handler;
  }

  onItemDrafted(handler: (data: ItemDraftedData) => void): void {
    this.onItemDraftedHandler = handler;
  }

  onDraftError(handler: (data: DraftErrorData) => void): void {
    this.onDraftErrorHandler = handler;
  }

  onStageComplete(handler: (data: StageCompleteData) => void): void {
    this.onStageCompleteHandler = handler;
  }

  onSessionComplete(handler: (data: SessionCompleteData) => void): void {
    this.onSessionCompleteHandler = handler;
  }

  onStateConflict(handler: (data: StateConflictData) => void): void {
    this.onStateConflictHandler = handler;
  }

  onError(handler: (error: string) => void): void {
    this.onErrorHandler = handler;
  }

  onConnectionChange(handler: (connected: boolean) => void): void {
    this.onConnectionChangeHandler = handler;
  }

  // ---------------------------------------------------------------------------
  // Accessors
  // ---------------------------------------------------------------------------

  getCurrentSessionId(): string | null {
    return this.currentSessionId;
  }

  isConnected(): boolean {
    return this.socket?.readyState === WebSocket.OPEN;
  }
}

export const onboardingWebSocketService = new OnboardingWebSocketService();

// ---------------------------------------------------------------------------
// Conversational correction — POST /improve-item
// ---------------------------------------------------------------------------
// Deliberately NOT part of the WS session protocol above: this is a single
// stateless request ("here's the current text, here's my feedback, give me
// a revision") with no stage, no progress, no multi-turn state — the same
// onboarding_orchestrator_service.py process just also exposes it as a
// plain REST route on the same port. The persistent smart wizard is the only
// UI that calls it; there is no separate correction modal or voice path.

const resolveOnboardingHttpBaseUrl = (): string => {
  const configuredUrl = config.websocket.onboardingHttpUrl || config.websocket.onboardingUrl;
  const relativeBase =
    typeof window !== 'undefined' && window.location?.origin
      ? window.location.origin
      : 'http://localhost';
  const url = new URL(configuredUrl, relativeBase);

  if (url.protocol === 'wss:') url.protocol = 'https:';
  if (url.protocol === 'ws:') url.protocol = 'http:';

  return url.href.replace(/\/$/, '');
};

export interface ImproveItemRequest {
  /** Plain-language label shown to the LLM for context, e.g. "What you sell"
   *  or "Objection" — not a machine key. */
  contextLabel: string;
  current: ImproveItemCurrent;
  instruction: string;
}

export type ImproveItemResponse =
  | { updated: ImproveItemCurrent; changes: string[]; needsInput?: never }
  | { needsInput: string; updated?: never; changes?: never };

export async function improveItem(request: ImproveItemRequest): Promise<ImproveItemResponse> {
  const token = await auth.currentUser?.getIdToken();
  if (!token) {
    throw new Error('Not signed in.');
  }

  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 30000);
  let response: Response;
  try {
    response = await fetch(`${resolveOnboardingHttpBaseUrl()}/improve-item`, {
      method: 'POST',
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(request),
    });
  } catch (error: any) {
    if (error?.name === 'AbortError') {
      throw new Error('The update took too long. Please try again.');
    }
    throw error;
  } finally {
    window.clearTimeout(timeout);
  }

  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new Error(body?.detail || 'Could not generate an update — try rephrasing.');
  }

  return response.json();
}
